/* eslint-disable react-hooks/exhaustive-deps */
import * as yup from 'yup';
import { useTranslate } from '@tolgee/react';
import { ReactNode, useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TournamentCategoryItemFragment } from '@amf/shared/types/graphql';
import { Regions } from '@amf/shared/utils/region';
import Container from '@amf/shared/components/layout/Container';
import TextField from '@amf/shared/components/form/TextField';
import ButtonIcon from '@amf/shared/components/button/ButtonIcon';
import SelectField from '@amf/shared/components/form/SelectField';

const schema = yup.object({
  query: yup
    .string()
    .nullable()
    .transform((_, value) => (value === '' ? null : value)),
  dateFrom: yup
    .string()
    .nullable()
    .transform((_, value) => (value === '' ? null : value)),
  dateTo: yup
    .string()
    .nullable()
    .transform((_, value) => (value === '' ? null : value)),
  category: yup.string(),
  region: yup.string(),
});

export interface TournamentFilterData {
  query: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  category: string;
  region: string;
}

export const defaultValues: TournamentFilterData = {
  query: null,
  dateFrom: null,
  dateTo: null,
  category: '',
  region: '',
};

interface TournamentFilterProps {
  title: ReactNode;
  button: ReactNode;
  categories: TournamentCategoryItemFragment[];
  onUpdate: (data: TournamentFilterData) => Promise<void>;
  children?: ReactNode;
}

export default function TournamentFilter({
  title,
  button,
  categories,
  onUpdate,
  children,
}: TournamentFilterProps) {
  const [counter, setCounter] = useState(0);
  const { t } = useTranslate('malyfotbal');
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<TournamentFilterData>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const query = useWatch({ control, name: 'query' });
  const dateFrom = useWatch({ control, name: 'dateFrom' });
  const dateTo = useWatch({ control, name: 'dateTo' });
  const category = useWatch({ control, name: 'category' });
  const region = useWatch({ control, name: 'region' });

  useEffect(() => {
    if (counter > 0) {
      handleSubmit(onUpdate)();
    }
    setCounter(counter + 1);
  }, [query, dateFrom, dateTo, category, region]);

  return (
    <form onSubmit={handleSubmit(onUpdate)}>
      <Container>
        <div className='TournamentFilter__header'>
          {title}
          <div className='TournamentFilter__headerActions'>
            <Controller
              name='query'
              control={control}
              render={({ field }) => (
                <TextField
                  className='TournamentFilter__search'
                  label={t('tournaments.filter.search')}
                  type='search'
                  fieldSize='small'
                  error={errors.query?.message}
                  accessory={
                    <ButtonIcon>
                      <i className='bi bi-search' />
                    </ButtonIcon>
                  }
                  {...field}
                />
              )}
            />
            {button}
          </div>
        </div>
      </Container>
      <Container className='TournamentFilter__container'>
        <div className='TournamentFilter'>
          <div className='TournamentFilter__panel'>
            <Controller
              name='dateFrom'
              control={control}
              render={({ field }) => (
                <TextField
                  label={t('tournaments.filter.dateFrom')}
                  type='date'
                  error={errors.dateFrom?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name='dateTo'
              control={control}
              render={({ field }) => (
                <TextField
                  label={t('tournaments.filter.dateTo')}
                  type='date'
                  error={errors.dateTo?.message}
                  {...field}
                />
              )}
            />
            <Controller
              name='category'
              control={control}
              render={({ field }) => (
                <SelectField
                  label={t('tournaments.filter.category')}
                  error={errors.category?.message}
                  {...field}
                >
                  <option value={''}>{t('tournaments.filter.category.empty')}</option>
                  {categories.map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </SelectField>
              )}
            />
            <Controller
              name='region'
              control={control}
              render={({ field }) => (
                <SelectField
                  label={t('tournaments.filter.region')}
                  error={errors.region?.message}
                  {...field}
                >
                  <option value={''}>{t('tournaments.filter.region.empty')}</option>
                  {Regions.map(key => (
                    <option key={key} value={key}>
                      {t(`region.${key}`)}
                    </option>
                  ))}
                </SelectField>
              )}
            />
          </div>
        </div>
      </Container>
      {children}
    </form>
  );
}
